import {
    duckBanzai,
    duckSwitch,
    executeMultiKillSequence,
    renderInitialLineOfFire,
    slideLineOfFire,
    spawnNewLineItems,
    createDuckLineSlots, clearLineOfFire, protectDuck, exposeDuck
} from "@/presentation/renderers/gameSceneRendererLineOfFire.js";
import {
    drawCard,
    updateCardSelectionVisuals
} from "@/presentation/renderers/gameSceneRenderer.js";
import { createDeckCounter } from "@/presentation/prefabs/ui/deckCounter.js";
import { createTarget } from "@/presentation/prefabs/actor/target.js";
import { getLineSlots, getActionCardsInHand } from "@/presentation/helpers/selectors.js";
import { GAME_EVENTS } from "@/game-rules/constants/gameEvents.js";
import { ACTOR_TAGS } from "@/presentation/helpers/actorTags.js";
import { handleBotTurn, handleHumanTurn } from "@/presentation/scenes/game/gameEventTurnUtils.js";
import { calculateActionCardPosition } from "@/presentation/renderers/gameSceneRendererUtils.js";

export function bindGameEvents(k, gameState, uiElements) {
    // --- GAME START ---
    gameState.addEventListener(GAME_EVENTS.GAME_STARTED, async () => {
        uiElements.duckDeckCounter = createDeckCounter(k, gameState.duckDeck.length, {
            x: k.width() - 120,
            y: 10,
            iconType: "sprite",
            spriteName: "sheep"
        });

        uiElements.actionDeckCounter = createDeckCounter(k, gameState.actionDeck.length, {
            x: k.width() - 120,
            y: 62,
            iconType: "rect"
        });

        createDuckLineSlots(k);

        await renderInitialLineOfFire(k, gameState.lineOfFire);
        gameState.startInitialTurn();

        console.log("Game initialized with teams:", gameState.activeTeams);
        console.log("Players successfully initialized:", gameState.players);
        console.log("Line of Fire:", gameState.lineOfFire);

        console.log("======== DECKS =========");
        console.log("DuckDeck:", gameState.duckDeck);
        console.log("ActionDeck:", gameState.actionDeck);
    });

    // --- TURN MANAGEMENT ---
    gameState.addEventListener(GAME_EVENTS.TURN_STARTED, async () => {
        const activePlayer = gameState.getCurrentPlayer();
        const playableCards = gameState.getPlayableCard();

        // Mise à jour de l'UI commune
        uiElements.turnBanner.updateStatus(activePlayer, gameState.duckDiscardPile);
        const cur = k.get("custom_cursor")[0];

        if (activePlayer.isBot) {
            if (cur) cur.hidden = true;
            await handleBotTurn(k, gameState, playableCards);
        } else {
            if (cur) cur.hidden = false;
            await handleHumanTurn(k, gameState, playableCards);
        }
    });

    // --- LINE UPDATES ---
    // *** Card actions shooting : PAN, TIREUR_DELITE, CANARDAGE ***
    gameState.addEventListener(GAME_EVENTS.SHOT_RESOLUTION, async (event) => {
        const kills = event.detail.kills;
        const waterHits = event.detail.waterHits;
        const cardTypeId = event.detail.cardTypeId;
        const activePlayer = gameState.getCurrentPlayer();

        await executeMultiKillSequence(k, kills, waterHits, cardTypeId, gameState);

        if (uiElements.duckDeckCounter) {
            uiElements.duckDeckCounter.updateCount(gameState.duckDeck.length);
        }

        if (uiElements.turnBanner) {
            uiElements.turnBanner.updateStatus(activePlayer, gameState.duckDiscardPile);
        }

        const winner = gameState.checkWinner();

        if (winner) {
            k.go("gameOver", winner);
            return;
        }

        gameState.drawActionCardManager();
    });

    // *** Card action : EN_AVANT_MARCHE ***
    gameState.addEventListener(GAME_EVENTS.FORWARD_MARCH, async () => {
        const activePlayer = gameState.getCurrentPlayer();

        await slideLineOfFire(k, [0, 1, 2, 3, 4, 5], 1);
        await spawnNewLineItems(k, [5], gameState);

        if (uiElements.duckDeckCounter) {
            uiElements.duckDeckCounter.updateCount(gameState.duckDeck.length);
        }

        if (uiElements.turnBanner) {
            uiElements.turnBanner.updateStatus(activePlayer, gameState.duckDiscardPile);
        }

        gameState.drawActionCardManager();
    });

    // *** Card action resolution = DISPERTION ***
    gameState.addEventListener(GAME_EVENTS.DISPERTION, async () => {
        await clearLineOfFire(k);

        for (let i = 0; i < 6; i++) {
            await spawnNewLineItems(k, [i], gameState);
        }

        const activePlayer = gameState.getCurrentPlayer();

        if (uiElements.duckDeckCounter) {
            uiElements.duckDeckCounter.updateCount(gameState.duckDeck.length);
        }

        if (uiElements.turnBanner) {
            uiElements.turnBanner.updateStatus(activePlayer, gameState.duckDiscardPile);
        }

        gameState.drawActionCardManager();
    });

    // *** Card action resolution = PLANQUE ***
    gameState.addEventListener(GAME_EVENTS.PROTECT_DUCK, async (e) => {
        const index = e.detail.slotIndex;
        await protectDuck(k, index);

        gameState.drawActionCardManager();
    });

    gameState.addEventListener(GAME_EVENTS.EXPOSE_DUCK, async (e) => {
        const lineOfFireSlotsIndex = e.detail.lineOfFireSlotsIndex;
        await exposeDuck(k, lineOfFireSlotsIndex);
    });

    // *** Card action resolution : EN_JOUE, DOUBLE_VISEUR, RECTIFIER_TIR_DROITE, RECTIFIER_TIR_GAUCHE ***
    gameState.addEventListener(GAME_EVENTS.TARGET_ADDED, (e) => {
        const lineSlots = getLineSlots(k);
        const lineSlotsIndex = e.detail.slotsIndex;
        const preventDraw = e.detail.preventDraw; // Used for dev cheat

        let animationsCompleted = 0;

        lineSlotsIndex.forEach(targetSlotIndex => {
            const slot = lineSlots.find(s => s.slotIndex === targetSlotIndex);

            if (slot) {
                const token = createTarget(k, slot.pos, targetSlotIndex);

                // Target spawn animation
                token.spawn(slot.pos.y - 50, () => {
                    animationsCompleted++;

                    if (animationsCompleted === lineSlotsIndex.length && !preventDraw) {
                        gameState.drawActionCardManager();
                    }
                });
            } else {
                console.warn(`Could not find target slot visually for index ${targetSlotIndex}`);
            }
        });
    });

    gameState.addEventListener(GAME_EVENTS.TARGET_REMOVED, (event) => {
        const targetSlotsIndex = event.detail.slotsIndex;
        const tokens = k.get(ACTOR_TAGS.TARGET);

        targetSlotsIndex.forEach(targetSlotIndex => {
            const tokenToDestroy = tokens.find(t => t.slotIndex === targetSlotIndex);

            if (tokenToDestroy && tokenToDestroy.removeToken) {
                tokenToDestroy.removeToken();
            }
        });
    });

    // *** Card action resolution = PALME_ARRIERE, PALME_AVANT ***
    gameState.addEventListener(GAME_EVENTS.SWAP_DUCK, async (event) => {
        const {duckFromIndex, duckToIndex} = event.detail;
        await duckSwitch(k, duckFromIndex, duckToIndex);
        gameState.drawActionCardManager();
    });

    // *** Card action resolution = BANZAI ***
    gameState.addEventListener(GAME_EVENTS.BANZAI, async (event) => {
        const duckIndex = event.detail.duckIndex;
        await duckBanzai(k, duckIndex, gameState);
        gameState.drawActionCardManager();
    });

    // --- CARD EVENT ---
    gameState.addEventListener(GAME_EVENTS.CARD_SELECTED, (event) => {
        const selectedIndex = event.detail.selectedIndex;
        updateCardSelectionVisuals(k, selectedIndex);

        const cur = k.get("custom_cursor")[0];
        if (!cur || !cur.equip) return;

        const slots = getLineSlots(k);

        if (selectedIndex !== null) {
            const activePlayer = gameState.getCurrentPlayer();
            const card = activePlayer.hand[selectedIndex];

            cur.equip(card.typeId);

            slots.forEach(slot => {
                const isValid = card.isValidOn(slot.slotIndex, gameState);
                slot.setHighlight(isValid, k.Color.fromHex(card.colorHex));
            });
        } else {
            cur.equip(null);

            slots.forEach(slot => {
                slot.setHighlight(false);
            });
        }
    });

    gameState.addEventListener(GAME_EVENTS.CARD_PLAYED, (event) => {
        const { index, targetIndex } = event.detail;
        const playerHand = getActionCardsInHand(k);
        const cardToAnimate = playerHand.find(c => c.handIndex === index);

        if (cardToAnimate) {
            cardToAnimate.untag(ACTOR_TAGS.ACTION_CARD);
            const lineSlots = getLineSlots(k);
            const targetPos = lineSlots.find(s => s.slotIndex === targetIndex).pos;

            // Fly the card to the duck!
            k.tween(cardToAnimate.pos, targetPos, 0.4, (p) => cardToAnimate.pos = p, k.easings.easeInQuart);
            k.tween(cardToAnimate.scale, k.vec2(0), 0.4, (s) => cardToAnimate.scale = s);

            const activePlayer = gameState.getCurrentPlayer();
            const remainingPlayerHand = getActionCardsInHand(k);

            // Move remaning cards to the left
            remainingPlayerHand.forEach((actionCard, newIdx) => {
                actionCard.setIsPlayableEffect(false) // remove playble hint
                actionCard.handIndex = newIdx; // Assign new index
                const newTargetX = calculateActionCardPosition(k, newIdx, activePlayer.hand.length).x;

                k.tween(
                    actionCard.pos.x,
                    newTargetX,
                    0.3,
                    (v) => actionCard.pos = k.vec2(v, actionCard.pos.y),
                    k.easings.easeOutQuad
                );
            });

            k.wait(0.4, () => {
                k.destroy(cardToAnimate);
            });
        }
    });

    gameState.addEventListener(GAME_EVENTS.CARD_DRAWN, async (event) => {
        const { newCard, playerIndex } = event.detail;
        const activePlayer = gameState.players[playerIndex];

        if (uiElements.actionDeckCounter) {
            uiElements.actionDeckCounter.updateCount(gameState.actionDeck.length);
        }

        await drawCard(k, activePlayer, newCard);
        await k.wait(1.5);
        gameState.endTurn();
    });
}